import { Box, Card, CardContent, ListItemButton, Switch, Typography, styled } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import React, { PropsWithChildren, useState } from 'react';
import { Collapsible } from './index';

export interface CollapsibleCardRow {
    key: string;
    value?: string;
    element?: JSX.Element;
}

export interface CollapsibleCardToggle {
    name: string;
    checkedState: any;
    onChange: (event: any) => void;
}

export interface CollapsibleCardInterface {
    title: string;
    titleIcon?: JSX.Element;
    id: string;
    rows: CollapsibleCardRow[];
    toggle?: CollapsibleCardToggle;
    defaultExpanded?: boolean;
}

export const CardStyled = styled(Card)`
    margin-top: 12px;
    max-width: 500px;
`;

export const CardContentStyled = styled(CardContent)`
    padding: 12px 16px;

    &:last-child {
        padding-bottom: 12px;
    }
`;

const ExpandMoreButton = styled(ListItemButton)`
    display: flex;
    justify-content: flex-start;
    font-size: 16px;

    padding: 0;
    border: 0;
    outline: 0;

    svg:first-of-type {
        margin-right: 8px;
    }

    &:hover {
        background-color: transparent;
    }
`;

export const CardTitle = styled(Typography)`
    color: ${({ theme }) => theme.palette.text.primary};
    width: 100%;
`;

const CollapsedContent = styled(Box)`
    margin-top: 14px;

    &:last-child {
        padding-bottom: 0;
    }
`;

const CardRow = styled(Box)`
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    padding-bottom: 8px;
`;

const TitleRow = styled(Box)`
    display: flex;
    flex-wrap: nowrap;
    flex-direction: row;
    align-items: center;
`;

export const CollapsibleCard = (props: PropsWithChildren<CollapsibleCardInterface>) => {
    const { id, title, titleIcon, toggle, rows, defaultExpanded, children } = props;
    const [expanded, setExpanded] = useState(toggle?.checkedState || defaultExpanded);

    const onClickExpandCard = (event?: any) => {
        setExpanded((previous) => !previous);
        toggle?.onChange(event);
    };

    return (
        <CardStyled variant="outlined">
            <CardContentStyled>
                {toggle ? (
                    <TitleRow>
                        <Switch name={toggle.name} checked={toggle.checkedState} onChange={onClickExpandCard} />
                        <CardTitle>{title}</CardTitle>
                    </TitleRow>
                ) : (
                    <ExpandMoreButton aria-controls={id} onClick={onClickExpandCard}>
                        {titleIcon}
                        <CardTitle>{title}</CardTitle>
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </ExpandMoreButton>
                )}
                {rows.length > 0 && (
                    <Collapsible id={id} expanded={expanded}>
                        <CollapsedContent>
                            {rows.map((row) => (
                                <CardRow key={row.key}>
                                    {row.element
                                        ? row.element
                                        : [
                                              <Typography key="key" color="text.primary">
                                                  {row.key}
                                              </Typography>,
                                              <Typography key="value" color="text.secondary">
                                                  {row.value}
                                              </Typography>
                                          ]}
                                </CardRow>
                            ))}
                            {children}
                        </CollapsedContent>
                    </Collapsible>
                )}
            </CardContentStyled>
        </CardStyled>
    );
};
